// The printed «Πρόγραμμα» of a day (owner report 22 Oct 2026): blocks with notes and short durations were drawn on top
// of each other on paper (a break 12:00–12:15 followed by calls 12:15–13:00 with notes). The printed plan is therefore
// a LIST, not a time grid: one row per item — time · type · title (and person) · notes, all wrapped — whose height
// grows with its content. Rows follow each other (they never share a vertical range), and a row is never cut by a
// page break: when it does not fit on the page, it starts the next one.
//
// Pure: `sheetRows` turns the day's plan items into rows; `layoutSheet` places them on pages with a conservative estimate
// of the wrapped text height (the printed page then uses normal flow + break-inside: avoid, and starts a new page where
// the layout says, so paper and the model agree). Tested in tests/unit/plan-sheet.test.ts; the real Chromium boxes in
// the E2E «planner v2».
import type { PlanItem, PlanType } from './planner.ts';

export interface SheetRow {
  key: string; start: string; end: string; type: PlanType; title: string; who: string; notes: string;
  done: boolean; committed: boolean; overlap: boolean;
  /** the printed name of the type (it wraps in its own narrow column) */
  typeText: string;
}
/** items → rows, in time order (overlapping items stay one after the other, marked, never side by side) */
export function sheetRows(items: readonly PlanItem[], label: (x: PlanItem) => { title: string; who: string; typeText?: string }): SheetRow[] {
  return [...items].filter((x) => x.status !== 'cancelled')
    .sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end) || a.key.localeCompare(b.key))
    .map((x) => { const l = label(x); return { key: x.key, start: x.start, end: x.end, type: x.type, title: l.title, who: l.who, notes: String(x.notes ?? '').trim(), done: x.status === 'done', committed: x.committed, overlap: x.conflictWith.length > 0, typeText: l.typeText ?? '' }; });
}

export interface SheetMetrics {
  /** usable page height (px at 96 dpi) and where the rows start on the first page (after the header) */
  pageHeight: number; firstTop: number;
  lineHeight: number; notesLineHeight: number; padding: number; minRow: number;
  /** characters per line of the title column and of the notes (estimated low, so the estimate errs on the tall side) */
  titleChars: number; notesChars: number;
  /** the type column (its own lines, smaller text) */
  typeChars: number; typeLineHeight: number;
  /** notes longer than this many lines are cut (with «…») so one row always fits on a page */
  maxNotesLines: number;
}
/** A4 portrait, 15 mm + 17 mm margins, 10 pt text: the printed plan's own metrics (core/print.ts uses the same CSS) */
export const A4_SHEET: SheetMetrics = { pageHeight: 1000, firstTop: 300, lineHeight: 19, notesLineHeight: 17, padding: 14, minRow: 40, titleChars: 52, notesChars: 70, maxNotesLines: 24, typeChars: 15, typeLineHeight: 17 };

/** how many lines a text takes at `chars` per line (word wrap; hard line breaks kept; long words broken) */
export function wrapLines(text: string, chars: number): number {
  if (!text) return 0;
  let lines = 0;
  for (const para of text.split(/\r?\n/)) {
    let cur = 0, n = 1;
    for (const w of para.split(/\s+/).filter(Boolean)) {
      let len = [...w].length;
      if (cur && cur + 1 + len <= chars) { cur += 1 + len; continue; }
      if (cur) { n++; cur = 0; }
      while (len > chars) { n++; len -= chars; }
      cur = len;
    }
    lines += n;
  }
  return lines;
}
/** the notes as printed (cut to `maxNotesLines` lines) */
export function sheetNotes(notes: string, m: SheetMetrics): string {
  if (wrapLines(notes, m.notesChars) <= m.maxNotesLines) return notes;
  let lo = 0, hi = notes.length;
  while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (wrapLines(notes.slice(0, mid) + ' …', m.notesChars) <= m.maxNotesLines) lo = mid; else hi = mid - 1; }
  return notes.slice(0, lo).trimEnd() + ' …';
}
export function sheetRowHeight(r: SheetRow, m: SheetMetrics): number {
  const title = Math.max(1, wrapLines([r.title, r.who].filter(Boolean).join(' · '), m.titleChars));
  const notes = wrapLines(sheetNotes(r.notes, m), m.notesChars);
  const type = wrapLines(r.typeText, m.typeChars) * m.typeLineHeight;
  return Math.max(m.minRow, Math.ceil(m.padding + Math.max(type, title * m.lineHeight + (notes ? 4 + notes * m.notesLineHeight : 0))));
}
export interface PlacedRow { key: string; page: number; top: number; bottom: number }
/** rows placed one after the other; a row that does not fit starts the next page (never cut) */
export function layoutSheet(rows: readonly SheetRow[], m: SheetMetrics = A4_SHEET): { pages: number; rows: PlacedRow[] } {
  const out: PlacedRow[] = [];
  let page = 0, y = m.firstTop;
  for (const r of rows) {
    const h = sheetRowHeight(r, m);
    if (y + h > m.pageHeight && y > 0) { page++; y = 0; }
    out.push({ key: r.key, page, top: y, bottom: y + h });
    y += h;
  }
  return { pages: page + 1, rows: out };
}
